import React, { useState, useCallback } from 'react';

import classNames from 'classnames';
import * as fromProjects from 'store/Projects';

import { ProjectTitleBlock } from 'components/Project/ProjectTitleSections/ProjectTitleBlock';

import * as fromProjectContentSections from 'components/Project/ProjectContentSections/Index';

interface IOwnSpecificProps {
  animateIn: boolean;
  contentSection: fromProjects.IContentSection;
  contentBlock: fromProjects.IContentBlock;
  contentBlockIndex: number;
}

type IOwnProps = IOwnSpecificProps;

type IProps = IOwnProps;

export const ProjectContentBlock: React.FC<IProps> = ({ animateIn, contentSection, contentBlock, contentBlockIndex, ...props }) => {
  const [extraTopPadding, setExtraTopPadding] = useState(false);
  const [sectionHeight, setSectionHeight] = useState(null);
  const [contentBlockSpacer, setContentBlockSpacer] = useState(null);
  const [stickyTitle, setStickyTitle] = useState(false);
  const [verticallyCentredTitle, setVerticallyCentredTitle] = useState(false);
  const [bodyWrapperToSectionExtents, setBodyWrapperToSectionExtents] = useState(false);

  const makeSectionTitleSticky = () => {
    setStickyTitle(true);
  };

  const expandBodyWrapperToSectionExtents = () => {
    setBodyWrapperToSectionExtents(true);
  };

  const verticallyCentreSectionTitle = () => {
    setVerticallyCentredTitle(true);
  };

  const addExtraTopPaddingToBlock = () => {
    setExtraTopPadding(true);
  };

  const addContentBlockSpacer = (element: JSX.Element) => {
    setContentBlockSpacer((
      <div className='contentSpacerWrapper'>
        {element}
      </div>
    ));
  };

  const getContentComponent = (componentName: fromProjects.IContentBodySectionTypes, componentVariation: string, index: number) => {
    const bodyContent = contentSection.contentBlocks[contentBlockIndex].bodySection[index].bodyContent;

    const contentComponentCommonProps = {
      key: index,
      bodyContent,
      animationReady: animateIn,
    };

    if (componentName === 'imageBoard') {
      componentVariation = componentVariation as fromProjects.IContentImageBoardVariations;
      if (componentVariation === 'threeScattered')
        return (
          <fromProjectContentSections.ImageBoard.ThreeScattered
            {...contentComponentCommonProps}
            expandBodyWrapperToSectionExtents={expandBodyWrapperToSectionExtents}
          />
        );
      else if (componentVariation === 'twoOnOne')
        return <fromProjectContentSections.ImageBoard.TwoOnOne {...contentComponentCommonProps} />;
      else if (componentVariation === 'citiesOfTheFutureClips')
        return <fromProjectContentSections.ImageBoard.CitiesOfTheFutureClips {...contentComponentCommonProps} />;
    } else if (componentName === 'imageSlider') {
      return <fromProjectContentSections.ImageSlider {...contentComponentCommonProps} sliderBackgroundShade={contentSection.backgroundShade} />;
    } else if (componentName === 'imageRevealSlider') {
      return <fromProjectContentSections.ImageRevealSlider {...contentComponentCommonProps} />;
    } else if (componentName === 'steppedLayout') {
      return <fromProjectContentSections.SteppedLayout {...contentComponentCommonProps} />;
    } else if (componentName === 'massiveScrollingImage') {
      return <fromProjectContentSections.MassiveScrollingImage {...contentComponentCommonProps} />;
    } else if (componentName === 'brandBoard') {
      componentVariation = componentVariation as fromProjects.IContentBrandBoardVariations;
      if (componentVariation === 'jlr')
        return <fromProjectContentSections.BrandBoard.JLR {...contentComponentCommonProps} />;
      else if (componentVariation === 'podiumValuations')
        return <fromProjectContentSections.BrandBoard.PodiumValuations {...contentComponentCommonProps} />;
    } else if (componentName === 'quotation') {
      return <fromProjectContentSections.Quotation {...contentComponentCommonProps} quotationBackgroundShade={contentSection.backgroundShade} />;
    } else if (componentName === 'timeline') {
      return <fromProjectContentSections.Timeline {...contentComponentCommonProps} />;
    } else if (componentName === 'singleLargeImage') {
      return <fromProjectContentSections.SingleLargeImage {...contentComponentCommonProps} />;
    } else if (componentName === 'overlappingImageBoard') {
      return <fromProjectContentSections.OverlappingImageBoard {...contentComponentCommonProps} />;
    } else if (componentName === 'fullBleedImage') {
      componentVariation = componentVariation as fromProjects.IContentFullBleedImageVariations;
      if (componentVariation === 'behind')
        return <fromProjectContentSections.FullBleedImage.Behind {...contentComponentCommonProps} addContentBlockSpacer={addContentBlockSpacer} />;
      else if (componentVariation === 'inline')
        return <fromProjectContentSections.FullBleedImage.Inline {...contentComponentCommonProps} />;
      else if (componentVariation === 'parallax')
        return <fromProjectContentSections.FullBleedImage.Parallax {...contentComponentCommonProps} />;
    } else if (componentName === 'stickyScrollingImage') {
      let previousSameComponent: boolean = false;
      if ((contentBlockIndex > 0) && (contentSection.contentBlocks[contentBlockIndex].bodySection[0].type === 'stickyScrollingImage') && (contentSection.contentBlocks[contentBlockIndex - 1].bodySection[contentSection.contentBlocks[contentBlockIndex - 1].bodySection.length - 1].type === 'stickyScrollingImage'))
        previousSameComponent = true;
      const titleContent: fromProjects.IContentTitleDefaultSection = contentSection.contentBlocks[contentBlockIndex].titleSection.titleContent as fromProjects.IContentTitleDefaultSection;
      const alignRight = (titleContent.align === 'left');
      return (
        <fromProjectContentSections.StickyScrollingImage
          {...contentComponentCommonProps}
          previousSameComponent={previousSameComponent}
          alignRight={alignRight}
          addContentBlockSpacer={addContentBlockSpacer}
          makeSectionTitleSticky={makeSectionTitleSticky}
          addExtraTopPaddingToBlock={addExtraTopPaddingToBlock}
        />
      );
    } else if (componentName === 'overlappingImages') {
      const titleContent: fromProjects.IContentTitleDefaultSection = contentSection.contentBlocks[contentBlockIndex].titleSection.titleContent as fromProjects.IContentTitleDefaultSection;
      const alignRight = (titleContent.align === 'left');
      return (
        <fromProjectContentSections.OverlappingImages
          {...contentComponentCommonProps}
          alignRight={alignRight}
          verticallyCentreSectionTitle={verticallyCentreSectionTitle}
        />
      );
    } else if (componentName === 'singleLargeVideo') {
      return <fromProjectContentSections.SingleLargeVideo {...contentComponentCommonProps} />;
    }
    return null;

  };

  //const getContentComponentCallback = useCallback(() => getContentComponent, []);

  const sectionStyles = { height: sectionHeight };

  const getBodySections = useCallback(() => {
    return contentBlock.bodySection.map((bodySection, index) => {
      const bodyComponentName = bodySection.type;
      const bodyComponentVariation = bodySection.bodyContent.variation;
      const BodyComponent = getContentComponent(bodyComponentName, bodyComponentVariation, index);

      return (
        <div
          className={classNames(
            'bodyContentWrapper',
            { verticallyCentredTitle },
            { side: contentBlock.bodySection[index].position === 'side' },
            { sideScroll: contentBlock.bodySection[index].position === 'sideScroll' },
            { extendToSectionWrapper: bodyWrapperToSectionExtents },
          )}
          key={index}
        >
          {BodyComponent}
        </div>
      );
    });
  }, [animateIn]);

  const titleComponentOptionalProps = {
    sticky: stickyTitle,
    verticallyCentred: verticallyCentredTitle,
  };

  //console.log('rerendering content block');

  return (
    <section
      className={classNames(
        'contentBlock',
        { extraTopPadding },
      )}
      style={sectionStyles}
    >
      <div
        className={classNames(
          'contentWrapper',
          { verticallyCentredTitle },
        )}
      >
        <ProjectTitleBlock
          contentSection={contentSection}
          contentBlock={contentBlock}
          animateIn={animateIn}
          titleComponentOptionalProps={titleComponentOptionalProps}
        />
        {contentBlockSpacer}
        {getBodySections()}
      </div>
    </section >
  );
};
